import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import EnterpriseMembersContext from './EnterpriseMembers.context';
import useQueryParams from 'hooks/useQueryParams';
import useSelect from 'hooks/useSelect';
import {
  Enterprise,
  ENTERPRISE_MEMBER_INVITATION_STATUS,
  EnterpriseBusinessUnit,
  EnterpriseMember,
  EnterpriseMemberInvitationStatus,
} from 'models/Enterprises';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import PencilIcon from 'icons/Pencil.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import TrashIcon from 'icons/Trash.icon';
import { useTranslation } from 'react-i18next';
import ActionMenu from 'components/ActionMenu';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useLocation } from 'react-router-dom';
import api from 'api';
import { ManageMemberDrawer } from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/subrouters/Enterprises/subrouters/Enterprise/pages/ManagePeople/components/ManageMemberDrawer';
import BellIcon from 'icons/Bell.icon';
import { getUserFullName } from 'utils';
import useRefetch from 'hooks/useRefetch';
import useUndoActions from 'hooks/useUndoActions';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import useEnterpriseBrokers from 'hooks/useEnterpriseBrokers';
import useBusinessUnits from 'hooks/useBusinessUnits';
import useMemberSeniorities from 'hooks/useMemberSeniorities';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useMemberLocations from 'hooks/useMemberLocations';
import { SendInvitatesOrRemindersModalRef } from './SendInvitatesOrRemindersModal/SendInvitatesOrRemindersModal.component';
import SendInvitatesOrRemindersModal from './SendInvitatesOrRemindersModal';

const DEFAULT_SORT = '-invitationStatusSort';

export const checkIsUserNotInvited = (
  invitationStatus: EnterpriseMemberInvitationStatus,
) => invitationStatus === ENTERPRISE_MEMBER_INVITATION_STATUS.NOT_INVITED;

export const checkUserHasInvitationSent = (
  invitationStatus: EnterpriseMemberInvitationStatus,
) => invitationStatus === ENTERPRISE_MEMBER_INVITATION_STATUS.INVITATION_SENT;

const checkIfEveryUserIsNotInvited = (members: EnterpriseMember[]) =>
  members.every(({ invitationStatus }) =>
    checkIsUserNotInvited(invitationStatus),
  );

const checkIfEveryUserHasInvitationSent = (members: EnterpriseMember[]) =>
  members.every(({ invitationStatus }) =>
    checkUserHasInvitationSent(invitationStatus),
  );

type EnterpriseMembersProps = {
  enterprise: Partial<Enterprise>;
  refetchFlag?: boolean;
  updateBusinessUnits: (
    updatedBusinessUnits: EnterpriseBusinessUnit[],
    shouldRefetch?: boolean,
  ) => void;
};

const EnterpriseMembers: React.FC<EnterpriseMembersProps> = (props) => {
  const { children, enterprise, refetchFlag: enterpriseRefetchFlag } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<EnterpriseMember[]>([]);
  const [totalPages, setTotalPages] = useState(0);

  const [isLoadingAddMember, setIsLoadingAddMember] = useState(false);
  const [isLoadingRemoveMembers, setIsLoadingRemoveMembers] = useState(false);
  const [isLoadingUndoRemoveMembers, setIsLoadingUndoRemoveMembers] =
    useState(false);

  const [removeEnterpriseMembers, setRemoveEnterpriseMembers] = useState<
    EnterpriseMember[]
  >([]);

  const [selectedMember, setSelectedMember] = useState<EnterpriseMember>();
  const [managePeopleDrawer, managePeopleDrawerRef] =
    useCallbackRef<SideDrawerRef | null>(null);

  const [sendInvitatesOrRemindersModal, sendInvitatesOrRemindersModalRef] =
    useCallbackRef<SendInvitatesOrRemindersModalRef>();

  const { popServerError } = useContext(PopNotificationsContext);

  const getMixpanelProps = useGetMixpanelProps();

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const { t } = useTranslation();
  const pathname = useLocation();

  const enterpriseId = enterprise?._id;

  const {
    params: {
      sort,
      page,
      invitationStatus,
      enterpriseSeniorityId,
      enterpriseBusinessUnitId,
    },
    setQueryParam,
  } = useQueryParams();

  const {
    selected,
    isAllSelected,
    isSingleSelected,
    isSelectedMode,
    selectAll,
    removeAll,
    updateSelected,
    updateSelectedValue,
  } = useSelect<EnterpriseMember>(items);

  const notInvitedMembers = selected.filter(({ invitationStatus }) =>
    checkIsUserNotInvited(invitationStatus),
  );

  const hasNotInvitedMembers = !!notInvitedMembers.length;

  const isEmpty = !isLoading && !items?.length;

  const { refetchFlag, refetch } = useRefetch();

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const isRemoveDisabled =
    isUndoAvailable || isLoadingRemoveMembers || isLoadingUndoRemoveMembers;

  const onUndoDeleteRequest = useCallback(
    async () =>
      removeEnterpriseMembers?.length === 1
        ? api.enterprises.undoDeleteMember(removeEnterpriseMembers[0]?._id)
        : api.enterprises.undoBulkDeleteMembers(enterprise?._id),
    [enterprise?._id, removeEnterpriseMembers],
  );

  const onUndo = useCallback(async () => {
    try {
      setIsLoadingUndoRemoveMembers(true);
      clearUndoTimeout();
      await onUndoDeleteRequest();
      refetch();
      setRemoveEnterpriseMembers([]);
    } catch {
      return;
    } finally {
      setIsLoadingUndoRemoveMembers(false);
    }
  }, [clearUndoTimeout, onUndoDeleteRequest, refetch]);

  const deleteMembers = useCallback(
    async (members: EnterpriseMember[]) => {
      try {
        clearUndoTimeout();
        setRemoveEnterpriseMembers(members);
        setIsLoadingRemoveMembers(true);

        const membersIds = members.map((el) => el._id);

        membersIds?.length === 1
          ? await api.enterprises.deleteMember(membersIds[0])
          : await api.enterprises.bulkDeleteMembers(
              enterprise?._id,
              membersIds,
            );

        refetch();
        removeAll();
        setUndoTimeout(() => setRemoveEnterpriseMembers([]));
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoadingRemoveMembers(false);
      }
    },
    [
      enterprise?._id,
      clearUndoTimeout,
      refetch,
      removeAll,
      setUndoTimeout,
      popServerError,
    ],
  );

  const handleAddPeople = useCallback(async () => {
    try {
      setIsLoadingAddMember(true);

      const { data } = await api.enterprises.createInitialMember(enterpriseId);
      setSelectedMember(data);
      managePeopleDrawer?.setOpen(true);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoadingAddMember(false);
    }
  }, [managePeopleDrawer, enterpriseId, popServerError]);

  const handleEditMember = useCallback(
    (member: EnterpriseMember) => {
      setSelectedMember(member);
      managePeopleDrawer?.setOpen(true);
    },
    [managePeopleDrawer],
  );

  const handleUpdateMember = useCallback(
    (member: EnterpriseMember) => {
      const isEdit = items.find(({ _id }) => _id === member._id);

      if (isEdit) {
        setItems((old) => {
          updateSelectedValue(member);
          return old.map((el) => (el._id === member._id ? member : el));
        });
      } else refetch();

      setSelectedMember(null);
    },
    [items, refetch, updateSelectedValue],
  );

  const updateMembers = useCallback(
    (updatedMembers: EnterpriseMember[]) =>
      setItems((prev) =>
        prev.map((member) => {
          const updatedMember = updatedMembers.find(
            ({ _id }) => _id === member._id,
          );
          if (updatedMember) updateSelectedValue(updatedMember);
          return updatedMember || member;
        }),
      ),
    [updateSelectedValue],
  );

  const handleSendInvites = useCallback(
    async (members: EnterpriseMember[]) => {
      sendInvitatesOrRemindersModal.open(members, false);
    },
    [sendInvitatesOrRemindersModal],
  );

  const handleSendReminders = useCallback(
    async (members: EnterpriseMember[]) => {
      sendInvitatesOrRemindersModal.open(members, true);
    },
    [sendInvitatesOrRemindersModal],
  );

  const handleSendInvitesToNonInvited = useCallback(
    () => handleSendInvites(notInvitedMembers),
    [notInvitedMembers, handleSendInvites],
  );

  const checkIfMemberIsSelected = useCallback(
    (member: EnterpriseMember) =>
      !!selected.find(
        ({ _id }) => _id === (member as unknown as EnterpriseMember)._id,
      ),
    [selected],
  );

  const onSelectAll = useCallback(() => {
    if (isAllSelected) {
      removeAll();
    } else {
      selectAll();
    }
  }, [isAllSelected, removeAll, selectAll]);

  const actions = useMemo(
    () => [
      ...(isSingleSelected
        ? [
            {
              icon: PencilIcon,
              disabled: isRemoveDisabled,
              onClick: () => handleEditMember(selected[0]),
            },
          ]
        : []),
      ...(checkIfEveryUserHasInvitationSent(selected)
        ? [
            {
              icon: BellIcon,
              disabled: isRemoveDisabled,
              onClick: () => handleSendReminders(selected),
            },
          ]
        : []),
      ...(checkIfEveryUserIsNotInvited(selected)
        ? [
            {
              icon: ArrowrightIcon,
              disabled: isRemoveDisabled,
              onClick: () => handleSendInvites(selected),
            },
          ]
        : []),
      ...(!isUndoAvailable
        ? [
            {
              alert: true,
              disabled: isRemoveDisabled,
              icon: TrashIcon,
              onClick: () => deleteMembers(selected),
            },
          ]
        : []),
    ],
    [
      selected,
      isSingleSelected,
      isUndoAvailable,
      isRemoveDisabled,
      deleteMembers,
      handleEditMember,
      handleSendInvites,
      handleSendReminders,
    ],
  );

  const hasActionMenu = isSelectedMode && !!actions.length;

  const getDropdownOptions = useCallback(
    (members: EnterpriseMember[]) => [
      ...(members?.length === 1
        ? [
            {
              label: t('edit'),
              icon: <PencilIcon />,
              disabled: isRemoveDisabled,
              onClick: () => handleEditMember(members[0]),
            },
          ]
        : []),
      ...(checkIfEveryUserHasInvitationSent(members)
        ? [
            {
              label: t('sendReminders', { count: members?.length }),
              icon: <BellIcon />,
              disabled: isRemoveDisabled,
              onClick: () => handleSendReminders(members),
            },
          ]
        : []),
      ...(checkIfEveryUserIsNotInvited(members)
        ? [
            {
              label: t('sendInvites', { count: members?.length }),
              icon: <ArrowrightIcon />,
              disabled: isRemoveDisabled,
              onClick: () => handleSendInvites(members),
            },
          ]
        : []),
      ...(!isUndoAvailable
        ? [
            {
              label: t('remove'),
              alert: true,
              disabled: isRemoveDisabled,
              icon: <TrashIcon />,
              onClick: () => deleteMembers(members),
            },
          ]
        : []),
    ],
    [
      isUndoAvailable,
      isRemoveDisabled,
      t,
      handleEditMember,
      handleSendReminders,
      handleSendInvites,
      deleteMembers,
    ],
  );

  const brokers = useEnterpriseBrokers(enterprise);
  const { businessUnitsOptions } = useBusinessUnits(enterprise?.businessUnits);
  const { memberSenioritiesOptions } = useMemberSeniorities(
    enterprise?.memberSeniorities,
  );
  const { memberLocationsOptions } = useMemberLocations(
    enterprise?.memberLocations,
  );

  useEffect(() => {
    if (!sort) {
      setQueryParam('sort', DEFAULT_SORT);
    }
  }, [sort, setQueryParam]);

  useEffect(() => {
    removeAll();
  }, [pathname, page, sort, refetchFlag, enterpriseRefetchFlag, removeAll]);

  useEffect(() => {
    if (!sort || !enterpriseId) return;

    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.enterprises.getEnterprisePeople(enterpriseId, {
          $limit: limit,
          $page: page,
          $sort: sort,
          invitationStatus,
          enterpriseSeniorityId,
          enterpriseBusinessUnitId,
        });
        setItems(items);
        setTotalPages(totalPages);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    getMixpanelProps,
    limit,
    refetchFlag,
    enterpriseRefetchFlag,
    enterpriseId,
    sort,
    page,
    enterpriseSeniorityId,
    invitationStatus,
    enterpriseBusinessUnitId,
  ]);

  const contextValue = useMemo(
    () => ({
      enterprise,
      enterpriseId: enterprise?._id,
      members: items,
      isLoading,
      isEmpty,
      totalPages,
      hasNotInvitedMembers,
      isLoadingAddMember,
      isAllSelected,
      selected,
      isRemoveDisabled,
      brokers,
      limit,
      units: businessUnitsOptions,
      memberSeniorities: memberSenioritiesOptions,
      locations: memberLocationsOptions,
      getDropdownOptions,
      updateSelected,
      onSelectAll,
      handleEditMember,
      refetch,
      handleAddPeople,
      handleSendReminders,
      handleSendInvites,
      handleSendInvitesToNonInvited,
      checkIfMemberIsSelected,
      deleteMembers,
      updateLimit,
    }),
    [
      enterprise,
      items,
      isLoading,
      isEmpty,
      totalPages,
      hasNotInvitedMembers,
      isLoadingAddMember,
      isAllSelected,
      selected,
      isRemoveDisabled,
      brokers,
      limit,
      businessUnitsOptions,
      memberSenioritiesOptions,
      memberLocationsOptions,
      getDropdownOptions,
      updateSelected,
      onSelectAll,
      handleEditMember,
      refetch,
      handleAddPeople,
      handleSendReminders,
      handleSendInvites,
      handleSendInvitesToNonInvited,
      checkIfMemberIsSelected,
      deleteMembers,
      updateLimit,
    ],
  );

  return (
    <EnterpriseMembersContext.Provider value={contextValue}>
      {hasActionMenu && (
        <ActionMenu
          actions={actions}
          onClose={removeAll}
          label={t('selectedMembers', { count: selected.length })}
        />
      )}

      {children}

      <SendInvitatesOrRemindersModal
        ref={sendInvitatesOrRemindersModalRef}
        enterpriseId={enterpriseId}
        updateMembers={updateMembers}
      />

      <ManageMemberDrawer
        member={selectedMember}
        enterprise={enterprise}
        ref={managePeopleDrawerRef}
        onSuccess={handleUpdateMember}
        onClose={() => setSelectedMember(null)}
      />

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        disabled={isLoadingUndoRemoveMembers}
        onUndo={onUndo}
      >
        <p>
          {t('memberRemovedFromEnterprise', {
            count: removeEnterpriseMembers.length,
            brandName: enterprise?.brandName,
            memberName: getUserFullName(
              removeEnterpriseMembers[0]?.firstName,
              removeEnterpriseMembers[0]?.lastName,
            ),
          })}
        </p>
      </PopNotificationWithUndo>
    </EnterpriseMembersContext.Provider>
  );
};

export default EnterpriseMembers;
